import React from 'reactn';
import styled from 'styled-components';
import moment from 'moment-timezone';
import Modal from '../../common/components/Modal';
import Button from '../../common/components/Button';
import Tooltip from '../../common/components/Tooltip';
import TimesheetContainer from './TimesheetContainer';
import PopoverDecline from './DeclinePopover';
import {
  APPROVE_TIMESHEET_TIMESHEET_LINEITEM,
  REJECT_TIMESHEET_TIMESHEET_LINEITEM,
  CANCEL_TIMESHEET_TIMESHEET_LINEITEM,
} from '../mutations';
import TimesheetUploadsContainer from '../../timesheets/components/TimesheetUploadsContainer';
import { showError } from '../../common/notifications';

const WEEKDAY_TASKS = ['monTask', 'tueTask', 'wedTask', 'thuTask', 'friTask', 'satTask', 'sunTask'];

/**
 * Checks if a timesheet has hours logged on a future date (relative to 4pm EST cutoff).
 * This check is only performed if the feature flag is enabled.
 * Returns an object with:
 * - hasFutureHours: true if timesheet cannot be approved yet (and feature is enabled)
 * - earliestApprovalTime: formatted string of when approval will be allowed (if hasFutureHours is true)
 * - latestDate: the latest date with hours (YYYYMMDD format)
 */
const getTimesheetApprovalStatus = (timesheet, config) => {
  // Check if feature flag is enabled
  const configValue = config?.['timesheet-approval-date-validation'];
  const isFeatureEnabled = configValue === '1' || configValue === 'true';
  
  console.log('getTimesheetApprovalStatus:', {
    timesheetId: timesheet._id,
    configValue,
    isFeatureEnabled,
    hasLineItems: !!timesheet.lineItems,
    lineItemsLength: timesheet.lineItems?.length,
  });
  
  // If feature is disabled, skip validation
  if (!isFeatureEnabled) {
    console.log('Feature disabled - skipping validation');
    return { hasFutureHours: false, earliestApprovalTime: null, latestDate: null };
  }

  const { lineItems } = timesheet;
  
  // If no lineItems, allow approval
  if (!lineItems || lineItems.length === 0) {
    return { hasFutureHours: false, earliestApprovalTime: null, latestDate: null };
  }

  // Find all dates with hours > 0
  let latestDate = null;
  
  for (const lineItem of lineItems) {
    for (const taskKey of WEEKDAY_TASKS) {
      const task = lineItem[taskKey];
      if (task && task.hours > 0 && task.date) {
        // Dates are in YYYYMMDD format, so string comparison works for finding latest
        if (!latestDate || task.date > latestDate) {
          latestDate = task.date;
        }
      }
    }
  }

  // If no tasks with hours, allow approval
  if (!latestDate) {
    return { hasFutureHours: false, earliestApprovalTime: null, latestDate: null };
  }

  // Get current time in America/New_York timezone
  const nowInEST = moment().tz('America/New_York');

  // Create the cutoff time: 4pm EST on the latest date
  const cutoffTime = moment.tz(latestDate, 'YYYYMMDD', 'America/New_York').set({
    hour: 16,
    minute: 0,
    second: 0,
    millisecond: 0,
  });

  // Check if current time is before the cutoff
  const hasFutureHours = nowInEST.isBefore(cutoffTime);
  
  console.log('Timesheet approval status check:', {
    latestDate,
    nowInEST: nowInEST.format(),
    cutoffTime: cutoffTime.format(),
    hasFutureHours,
  });
  
  return {
    hasFutureHours,
    earliestApprovalTime: hasFutureHours ? cutoffTime.format('dddd, MMMM D [at] h:mm A z') : null,
    latestDate,
  };
};

const ActionsWrapper = styled.div`
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-start;
  width: 100px;
  > * {
    margin: 0.25em;
  }
`;

// given user and array of remaining approvers, return bool of where user needs to approve ts
const canCurrentUserApprove = (user, remainingEmailsThatCanApprove) =>
  remainingEmailsThatCanApprove.includes(user.emails[0].address);

// determine if ts buttons should be disabled
const shouldButtonBeDisabled = (timesheet, loading, user, isCancel = false) => {
  const { status } = timesheet;
  // if status of ts is approved or declined, disabled buttons (except for cancel button on approved timesheets)
  if (status === 'Approved') {
    // Allow cancel button for approved timesheets
    if (isCancel) {
      return loading; // Only disable if loading
    }
    return true;
  }

  // if buttons are in loading state, disable them
  if (loading) {
    return true;
  }

  // flatten all required approver emails
  const remainingEmailsThatCanApprove = timesheet.approvalsRequired.reduce((acc, cur) => {
    if (cur.status && cur.status === 'Approved') {
      // ignore
      return acc;
    }
    return [...acc, ...cur.canApprove];
  }, []);

  // if user is NOT required to approve ts, disable buttons
  if (!canCurrentUserApprove(user, remainingEmailsThatCanApprove)) {
    return true;
  }

  return false;
};

class TimesheetActions extends React.Component {
  state = {
    approveLoading: false,
    declineLoading: false,
    cancelLoading: false,
    visible: false,
    viewAttachment: false,
  };

  decline = comments => {
    this.setState({ declineLoading: true });
    const { client, timesheet } = this.props;
    return client
      .mutate({
        mutation: REJECT_TIMESHEET_TIMESHEET_LINEITEM,
        awaitRefetchQueries: true,
        refetchQueries: this.props.refetchQueries || [],
        variables: {
          timesheetId: timesheet._id,
          comments,
        },
      })
      .then(() => this.setState({ declineLoading: false }));
  };

  // cancel timesheet
  cancel = comments => {
    this.setState({ cancelLoading: true });
    const { client, timesheet } = this.props;
    return client
      .mutate({
        mutation: CANCEL_TIMESHEET_TIMESHEET_LINEITEM,
        awaitRefetchQueries: true,
        refetchQueries: this.props.refetchQueries || [],
        variables: {
          timesheetId: timesheet._id,
          comments,
        },
      })
      .then(() => this.setState({ cancelLoading: false }))
      .catch(error => {
        console.error('Error canceling timesheet:', error);
        this.setState({ cancelLoading: false });
      });
  };

  approve = () => {
    this.setState({ approveLoading: true });
    const { client, timesheet } = this.props;
    client
      .mutate({
        mutation: APPROVE_TIMESHEET_TIMESHEET_LINEITEM,
        awaitRefetchQueries: true,
        refetchQueries: this.props.refetchQueries || [],
        errorPolicy: 'all', // This allows us to catch errors even if mutation partially succeeds
        variables: {
          timesheetId: timesheet._id,
        },
      })
      .then((result) => {
        // Check for errors in the result even if promise resolves (errorPolicy: 'all')
        const errors = result.errors || result.data?.errors || [];
        if (errors.length > 0) {
          const errorMessage = errors[0].message || errors[0].extensions?.exception?.message || 'Failed to approve timesheet';
          console.log('GraphQL errors in result:', errors);
          showError({ 
            message: 'Cannot Approve Timesheet',
            description: errorMessage,
            duration: 6,
          });
          this.setState({ approveLoading: false });
          return; // Don't proceed if there are errors
        }
        this.setState({ approveLoading: false });
      })
      .catch(error => {
        console.error('Error approving timesheet:', error);
        console.error('Error details:', {
          graphQLErrors: error?.graphQLErrors,
          networkError: error?.networkError,
          message: error?.message,
          fullError: error,
        });
        this.setState({ approveLoading: false });
        
        // Extract error message from GraphQL error or network error
        let errorMessage = 'Failed to approve timesheet. Please try again.';
        
        // Try multiple ways to extract the error message
        if (error?.graphQLErrors && error.graphQLErrors.length > 0) {
          errorMessage = error.graphQLErrors[0].message;
        } else if (error?.networkError?.result?.errors && error.networkError.result.errors.length > 0) {
          errorMessage = error.networkError.result.errors[0].message;
        } else if (error?.networkError?.error?.result?.errors && error.networkError.error.result.errors.length > 0) {
          errorMessage = error.networkError.error.result.errors[0].message;
        } else if (error?.message) {
          errorMessage = error.message;
        }
        
        // Display error toast notification
        showError({ 
          message: 'Cannot Approve Timesheet',
          description: errorMessage,
          duration: 6,
        });
      });
  };

  showModal = () => {
    this.setState({
      visible: true,
    });
  };
  showAttachment = () => {
    this.setState({
      viewAttachment: true,
    });
  };
  hideAttachment = () => {
    this.setState({
      viewAttachment: false,
    });
  };

  handleOk = e => {
    this.setState({
      visible: false,
    });
  };

  handleCancel = e => {
    this.setState({
      visible: false,
    });
  };

  render() {
    const { user, config } = this.global;
    const { timesheet } = this.props;
    const { visible, declineLoading, approveLoading, cancelLoading, viewAttachment } = this.state;
    const attachments =
      timesheet.timesheetAttachments?.length || timesheet.timesheetExpense ? true : false;
    
    // Check if timesheet has future hours (cannot be approved yet)
    // This check is controlled by the 'timesheet-approval-date-validation' config flag
    // If config is not loaded yet, default to allowing approval (feature disabled)
    const approvalStatus = getTimesheetApprovalStatus(timesheet, config || {});
    const { hasFutureHours, earliestApprovalTime } = approvalStatus;
    
    // Determine the approve button tooltip
    const approveTooltip = hasFutureHours
      ? `Cannot approve until ${earliestApprovalTime}`
      : 'Approve';
    
    return (
      <ActionsWrapper>
        <Tooltip title="View timesheet details" trigger="hover">
          <Button
            shape="circle"
            icon="profile"
            href={`/reports/timesheets/${timesheet._id}`}
            target="_blank"
          />
        </Tooltip>

        <Tooltip title="Download Timesheet PDF" trigger="hover">
          <Button
            disabled={timesheet.status !== 'Approved' || !timesheet.documentUrl}
            href={timesheet.documentUrl}
            target="_blank"
            rel="noopener noreferrer"
            shape="circle"
            icon="download"
          />
        </Tooltip>

        <Tooltip
          title={attachments ? 'View timesheet attachments' : 'No attachments'}
          trigger="hover"
        >
          <Button
            onClick={() => {
              this.showAttachment();
              this.showModal();
            }}
            shape="circle"
            icon="paper-clip"
            disabled={!attachments}
          />
        </Tooltip>

        {/* Approve button - enabled for Submitted-Pending, Cancelled, Declined */}
        {/* Also disabled if timesheet has hours on future dates (before 4pm EST cutoff) */}
        <Tooltip title={approveTooltip} trigger="hover">
          <Button
            loading={approveLoading}
            disabled={
              hasFutureHours ||
              !['Submitted-Pending', 'Cancelled', 'Declined'].includes(timesheet.status) ||
              shouldButtonBeDisabled(
                timesheet,
                declineLoading || cancelLoading || approveLoading,
                user,
              )
            }
            shape="circle"
            icon="check"
            onClick={this.approve}
          />
        </Tooltip>

        {/* Decline button - enabled only for Submitted-Pending status */}
        <PopoverDecline
          btnClassName="decline-timesheet"
          cancelPopover="false"
          loading={declineLoading}
          disabled={
            timesheet.status !== 'Submitted-Pending' ||
            shouldButtonBeDisabled(
              timesheet,
              declineLoading || approveLoading,
              user,
              false,
            )
          }
          decline={this.decline}
        />

        {/* Cancel button - always shown, enabled only for Approved status */}
        <PopoverDecline
          btnClassName="cancel-timesheet"
          cancelPopover="true"
          loading={cancelLoading}
          disabled={timesheet.status !== 'Approved' || cancelLoading}
          decline={this.cancel}
        />

        <Modal
          title={`${timesheet.user.firstName} ${timesheet.user.lastName}`}
          width={1200}
          visible={visible}
          onOk={this.handleOk}
          onCancel={this.handleCancel}
          footer={null}
        >
          {viewAttachment && <TimesheetUploadsContainer timesheet={timesheet} readonly />}
          {!viewAttachment && <TimesheetContainer timesheetId={timesheet._id} />}
        </Modal>
      </ActionsWrapper>
    );
  }
}

export default TimesheetActions;
